import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import styled from "@emotion/styled"
import { sizer } from "@ninjaone/utils"
import Tooltip from "@ninjaone/webapp/src/js/includes/components/Tooltip"
import { Flex, Box } from "@ninjaone/webapp/src/js/includes/components/Styled"
import Text from "./Text"

export const VARIANTS = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  TERTIARY: "tertiary",
  ACCESSORY: "accessory",
  DANGER: "danger",
  ICON: "icon",
}

const getStylesByVariant = ({ theme, variant, disabled }) => {
  switch (variant) {
    case VARIANTS.ICON:
      return `
         border: transparent;
         background-color: transparent;
         &:hover {
           background-color: ${theme.color.divider};
         }
      `
    case VARIANTS.PRIMARY: {
      const primaryColor = disabled ? theme.color.lightGray : theme.color.primary["100"]
      return `
          background-color: ${primaryColor};
          border: 1px solid ${primaryColor};
          color: ${disabled ? theme.color.gray : theme.color.white["100"]};
          ${!disabled &&
            `&:hover {
              background-color: ${theme.color.primaryHover};
            }`}
        `
    }
    case VARIANTS.TERTIARY:
      return `
          background-color: transparent;
          border: 0;
          color: ${disabled ? theme.color.gray : theme.color.action};
          ${!disabled &&
            `&:hover {
            background-color: ${theme.color.lightGray}
          }`}
        `
    case VARIANTS.ACCESSORY:
      return `
          background-color: ${disabled ? theme.color.lightGray : theme.color.white["100"]};
          border: 1px solid ${theme.color.border};
          color: ${theme.color.black["075"]};
          ${!disabled &&
            `&:hover {
            background-color: ${theme.color.lightGray}
          }`}
        `
    case VARIANTS.DANGER:
      return `
          background-color: ${disabled ? theme.color.error["050"] : theme.color.error["100"]};
          border: transparent;
          color: ${theme.color.white["100"]};
          ${!disabled &&
            `&:hover {
            background-color: ${theme.color.errorHover}
          }`}
        `
    case VARIANTS.SECONDARY:
    default:
      return `
          background-color: ${disabled ? theme.color.lightGray : theme.color.white["100"]};
          border: 1px solid ${theme.color.border};
          color: ${disabled ? theme.color.gray : theme.color.action};
          ${!disabled &&
            `&:hover {
            background-color: ${theme.color.lightGray}
          }`}
        `
  }
}

function getButtonText(type) {
  switch (type) {
    case "apply":
      return "general.apply"
    case "save":
      return "general.saveChanges"
    case "close":
      return "general.close"
    case "cancel":
      return "general.cancel"
    default:
      return ""
  }
}

const StyledButton = styled.button`
  margin: 0;
  display: flex;
  height: auto !important;
  align-items: center;
  user-select: none;
  outline: none;
  padding: ${({ size }) => {
    switch (size) {
      case "xs":
        return sizer(1, 2)
      case "sm":
        return sizer(2)
      default:
        return sizer(3)
    }
  }};
  border-radius: ${sizer(1)};
  cursor: pointer;
  ${({ theme, _type, variant, disabled }) => {
    if (variant) {
      return getStylesByVariant({ theme, variant, disabled })
    } else {
      return `border: 1px solid ${
        ["close", "save", "create", "apply"].includes(_type) ? "transparent" : theme.color.border
      };
      color: ${
        disabled
          ? theme.color.gray
          : ["close", "cancel", "default"].includes(_type)
          ? theme.color.action
          : ["delete"].includes(_type)
          ? theme.color.red
          : theme.color.background
      };
      background-color: ${
        disabled
          ? theme.color.lightGray
          : ["close", "cancel", "default", "delete"].includes(_type)
          ? theme.color.background
          : theme.color.action
      };`
    }
  }}
`

export default function Button({
  labelToken,
  type = "default",
  variant,
  submit,
  onClick,
  disabled,
  linkTo,
  className,
  children,
  tooltip,
  onMouseDown,
  size,
  Icon,
}) {
  const buttonContent = children ?? (
    <Flex alignItems="center" justifyContent="center">
      {Icon && (
        <Box marginRight={sizer(1)}>
          <Icon size="sm" />
        </Box>
      )}
      <Text bold token={labelToken || getButtonText(type)} size="sm" {...(!tooltip && { lineHeight: "initial" })} />
    </Flex>
  )

  if (tooltip) {
    return (
      <Tooltip output={tooltip}>
        <StyledButton {...{ variant, onClick, onMouseDown, size }}>{buttonContent}</StyledButton>
      </Tooltip>
    )
  }

  if (linkTo) {
    return (
      <Link to={linkTo} {...{ disabled }}>
        <StyledButton type="button" {...{ _type: type, variant, disabled, className, onMouseDown, size }}>
          {buttonContent}
        </StyledButton>
      </Link>
    )
  } else {
    return (
      <StyledButton
        type={submit ? "submit" : "button"}
        {...{
          _type: type,
          variant,
          onClick,
          disabled,
          className,
          onMouseDown,
          size,
        }}
      >
        {buttonContent}
      </StyledButton>
    )
  }
}

Button.propTypes = {
  labelToken: PropTypes.string,
  type: PropTypes.oneOf(["apply", "save", "close", "cancel", "create", "delete", "default"]),
  size: PropTypes.oneOf(["xs", "sm"]),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  linkTo: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  variant: PropTypes.string,
  Icon: PropTypes.elementType,
}
